import { ApolloClient, HttpLink, from } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import {
  AUTH_KEY,
  AUTH_REDIRECT_URL_KEY,
  TEMP_SESSION_KEY
} from "shared/constants"
import cache, { errorsVar, isLoggedInVar } from "./cache"

const USER_FINGERPRINT_PARAM = "honkGUID"

const readUserFingerprint = () => {
  let userFingerprint = localStorage.getItem(USER_FINGERPRINT_PARAM)
  if (isEmpty(userFingerprint)) {
    userFingerprint =
      Math.random().toString(36).substring(2, 30) +
      Math.random().toString(36).substring(2, 30)
    localStorage.setItem(USER_FINGERPRINT_PARAM, userFingerprint)
  }
  return userFingerprint
}

// Custom fetch to append honkGuid and tmp_tag for easy fingerprinting in logs
const fetchWithFingerprint = (uri, options) => {
  const url = new URL(uri)
  url.searchParams.append(USER_FINGERPRINT_PARAM, readUserFingerprint())

  // Add tmp_tag if temp session exists
  const tmpTag = sessionStorage.getItem(TEMP_SESSION_KEY)
  if (tmpTag) {
    url.searchParams.append(TEMP_SESSION_KEY, tmpTag)
  }

  return fetch(url, options)
}

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem(AUTH_KEY)
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      "X-AUTHENTICATION": token ? token.toString() : ""
    }
  }
})

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_PLATFORM_BASE}/graphql`,
  fetch: fetchWithFingerprint
})

// Clear temp session on app initialization (on page refresh)
// This prevents TempSessionOverlay from showing after refresh
// Temp sessions should only persist during the same browser session, not across refreshes
sessionStorage.removeItem(TEMP_SESSION_KEY)
sessionStorage.removeItem("temp_session_email")

export const clearOnLogout = (clientContext) => {
  isLoggedInVar(false)
  localStorage.removeItem(AUTH_KEY)
  localStorage.removeItem(AUTH_REDIRECT_URL_KEY)
  sessionStorage.removeItem(TEMP_SESSION_KEY) // Clear temp session on logout
  sessionStorage.removeItem("temp_session_email") // Clear temp session email
  clientContext.clearStore()
}

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (
    !isEmpty(graphQLErrors) &&
    get(graphQLErrors, "[0].message") === "missing_authentication"
  ) {
    clearOnLogout(client)
    window.location.replace("/")
  }

  if (
    !isEmpty(graphQLErrors) &&
    get(graphQLErrors, "[0].message") === "rate_limit_exceeded"
  ) {
    errorsVar(["rate_limit_exceeded"])
  }

  if (graphQLErrors || networkError) {
    errorsVar(["general_error"])
  } else {
    errorsVar(null)
  }
})

const client = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache
})

export default client
